import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["weight", "volume"]

  static values = {
    weightMax: { type: Number, default: 9999 },
    volumeMax: { type: Number, default: 120 },
    decimals: { type: Number, default: 2 }
  }

  connect() {
    this.setupField(this.weightTarget, this.weightMaxValue)
    this.setupField(this.volumeTarget, this.volumeMaxValue)

    this.normalizeField(this.weightTarget, { finalize: true })
    this.normalizeField(this.volumeTarget, { finalize: true })
  }

  normalize(event) {
    const finalize = event?.type === "blur"
    this.normalizeField(event.target, { finalize })
  }

  setupField(input, maxValue) {
    if (!input) return

    input.setAttribute("inputmode", "decimal")
    input.setAttribute("min", "0")
    input.setAttribute("step", "0.01")
    input.setAttribute("max", String(maxValue))
  }

  normalizeField(input, { finalize = true } = {}) {
    if (!input) return

    const maxValue = this.maxForField(input)
    const cleaned = this.cleanDecimal(input.value, { allowTrailingSeparator: !finalize })

    if (cleaned === "") {
      input.value = ""
      input.setCustomValidity("")
      return
    }

    const hasTrailingSeparator = cleaned.endsWith(".")
    const numericSource = hasTrailingSeparator ? cleaned.slice(0, -1) : cleaned
    const parsedValue = Number(numericSource)

    let numericValue = parsedValue
    if (!Number.isFinite(numericValue)) {
      input.value = ""
      input.setCustomValidity("")
      return
    }

    if (numericValue < 0) numericValue = 0
    if (Number.isFinite(maxValue) && numericValue > maxValue) {
      numericValue = maxValue
    }

    const wasClamped = numericValue !== parsedValue
    if (finalize || wasClamped) {
      input.value = this.formatForInput(numericValue)
    } else {
      input.value = this.formatForTyping(cleaned, numericValue)
    }

    input.setCustomValidity("")
  }

  maxForField(input) {
    if (input === this.weightTarget) return this.weightMaxValue
    if (input === this.volumeTarget) return this.volumeMaxValue

    const parsed = Number(input.max)
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY
  }

  cleanDecimal(value, { allowTrailingSeparator = false } = {}) {
    const source = String(value || "").trim()
    if (!source || /[eE]/.test(source)) return ""
    if (allowTrailingSeparator && (source === "," || source === ".")) return "0."

    const canonicalSource = source.includes(",")
      ? source.replace(/\./g, "").replace(",", ".")
      : source

    const raw = canonicalSource.replace(/[^0-9.]/g, "")

    if (!raw) return ""

    const firstDot = raw.indexOf(".")
    const normalized = firstDot >= 0
      ? `${raw.slice(0, firstDot + 1)}${raw.slice(firstDot + 1).replace(/\./g, "")}`
      : raw

    const [integerPartRaw, decimalPartRaw = ""] = normalized.split(".")
    const integerPart = integerPartRaw.replace(/^0+(?=\d)/, "")
    const decimalPart = decimalPartRaw.slice(0, this.decimalsValue)
    const hasTrailingSeparator = allowTrailingSeparator && /[,.]/.test(source) && decimalPartRaw.length === 0

    if (decimalPart.length > 0) {
      return `${integerPart || "0"}.${decimalPart}`
    }

    if (hasTrailingSeparator) {
      return `${integerPart || "0"}.`
    }

    return integerPart
  }

  formatForInput(value) {
    const rounded = Number(value).toFixed(this.decimalsValue)
    return rounded.replace(/\.?0+$/, "").replace(".", ",")
  }

  formatForTyping(cleaned, numericValue) {
    if (cleaned.endsWith(".")) {
      return `${Math.trunc(numericValue)},`
    }

    if (cleaned.includes(".")) {
      const [integerPart, decimalPart = ""] = cleaned.split(".")
      const normalizedInteger = integerPart.replace(/^0+(?=\d)/, "") || "0"
      return `${normalizedInteger},${decimalPart}`
    }

    return String(Math.trunc(numericValue))
  }
}
